import { all, call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { createBroadcast, fetchBroadcast, stopBroadcastAction } from './broadcastActions';
import { Broadcast, CreateBroadcastPayload, FetchBroadcastsResponse } from './broadcastTypes';
import {
  createBroadcastRequest,
  fetchBroadcastRequest,
  stopBroadcastRequest
} from './broadcastApi';
import {
  fetchBroadcastsSuccess,
  setBroadcastFailure,
  setBroadcastStatus,
  stopBroadcast
} from './broadcastSlice';

import { secondsToTimer } from 'utils/pretty-timer';
import unixTimestamp from 'utils/unix-timestamp';

function* fetchBroadcastSaga() {
  try {
    const response: FetchBroadcastsResponse = yield call(fetchBroadcastRequest);

    const broadcast = response.broadcast.broadcast_item_id === undefined ? undefined : parseBroadcast(response);

    yield put(fetchBroadcastsSuccess(broadcast));
  } catch (error) {
    yield put(setBroadcastFailure(error));
  }
}

function* createBroadcastSaga(action: PayloadAction<CreateBroadcastPayload>) {
  const payload = action.payload;

  yield put(setBroadcastStatus('loading'));

  try {
    const response: FetchBroadcastsResponse = yield call(createBroadcastRequest, payload);

    const broadcast = parseBroadcast(response);

    yield put(fetchBroadcastsSuccess(broadcast));
    yield put(setBroadcastStatus('success'));
  } catch (error) {
    yield put(setBroadcastFailure(error));
  }
}

function* stopBroadcastSaga(action: PayloadAction<string>) {
  const itemId = action.payload;

  if (!itemId) return;

  try {
    yield call(stopBroadcastRequest, itemId);

    yield put(stopBroadcast());
  } catch (error) {
    yield put(setBroadcastFailure(error));
  }
}

export function* broadcastSagas() {
  yield all([
    takeLatest(fetchBroadcast, fetchBroadcastSaga),
    takeLatest(createBroadcast, createBroadcastSaga),
    takeLatest(stopBroadcastAction, stopBroadcastSaga)
  ]);
}

function parseBroadcast(broadcast: FetchBroadcastsResponse): Broadcast {
  const {
    broadcast: { broadcast_item_id, status, statistics, duration_in_minutes, title, url, content }
  } = broadcast;

  return {
    id: broadcast_item_id,
    isRunning: status.running,
    statistics,
    timeLeft: status.time_left_sec !== undefined ? secondsToTimer(status.time_left_sec) : '0',
    startedAt:
      status.started_at !== undefined ? unixTimestamp.toDate(status.started_at) : undefined,
    duration: duration_in_minutes || 0,
    title,
    url,
    contentText: content?.[0]?.text || ''
  };
}
